import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  CHART_TYPES,
  CHART_VISIBILITIES,
  MAX_CHART_TYPES,
  chartTypeLabel,
  chartVisibilityLabel,
  isValidChartSlug,
  normalizeChartSlug,
  normalizeChartTypesSelection,
  type ChartDefinitionRow,
  type ChartType,
  type ChartVisibility,
} from '../../lib/chartDefinitions';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

type FieldOption = { key: string; label: string; options: string[] };
type ChannelOption = { slug: string; label: string };

export default function ChartDefinitionModal({
  isOpen,
  onClose,
  chart,
  fieldOptions,
  channelOptions,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  chart: ChartDefinitionRow | null;
  fieldOptions: FieldOption[];
  channelOptions: ChannelOption[];
  onSuccess: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [teaser, setTeaser] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [chartTypes, setChartTypes] = useState<ChartType[]>(['horizontal_bar']);
  const [defaultType, setDefaultType] = useState<ChartType>('horizontal_bar');
  const [audienceChannel, setAudienceChannel] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [visibility, setVisibility] = useState<ChartVisibility>('internal');
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (chart) {
      setSlug(chart.slug);
      setTitle(chart.title);
      setTeaser(chart.teaser ?? '');
      setFieldKey(chart.field_key);
      setChartTypes(chart.chart_types?.length ? chart.chart_types : [chart.chart_type]);
      setDefaultType(chart.chart_type);
      setAudienceChannel(chart.audience_channel ?? '');
      const entries = Object.entries(chart.audience_filter);
      setFilterKey(entries[0]?.[0] ?? '');
      setFilterValue(entries[0]?.[1] ?? '');
      setVisibility(chart.visibility);
      setSortOrder(chart.sort_order);
      setActive(chart.active);
    } else {
      setSlug('');
      setTitle('');
      setTeaser('');
      setFieldKey(fieldOptions[0]?.key ?? '');
      setChartTypes(['horizontal_bar']);
      setDefaultType('horizontal_bar');
      setAudienceChannel('');
      setFilterKey('');
      setFilterValue('');
      setVisibility('internal');
      setSortOrder(0);
      setActive(true);
    }
  }, [isOpen, chart, fieldOptions]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleTitleBlur = () => {
    if (!chart && title.trim() && !slug.trim()) {
      setSlug(normalizeChartSlug(title));
    }
  };

  const toggleChartType = (t: ChartType) => {
    setChartTypes((prev) => {
      if (prev.includes(t)) {
        if (prev.length === 1) return prev;
        const next = prev.filter((x) => x !== t);
        if (!next.includes(defaultType)) setDefaultType(next[0]);
        return next;
      }
      if (prev.length >= MAX_CHART_TYPES) {
        toast.error(`Pick at most ${MAX_CHART_TYPES} formats`);
        return prev;
      }
      return [...prev, t];
    });
  };

  const handleSave = async () => {
    const normalizedSlug = normalizeChartSlug(slug || title);
    if (!isValidChartSlug(normalizedSlug)) {
      toast.error('Slug must be 2–64 chars: lowercase letters, digits, underscores');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!fieldKey) {
      toast.error('Pick a custom field');
      return;
    }
    if (chartTypes.length < 1) {
      toast.error('Pick at least one display format');
      return;
    }

    const orderedTypes = normalizeChartTypesSelection(chartTypes, defaultType);
    const audience_filter: Record<string, string> = {};
    if (filterKey.trim() && filterValue.trim()) {
      audience_filter[filterKey.trim()] = filterValue.trim();
    }

    setSaving(true);
    try {
      const payload = {
        slug: normalizedSlug,
        title: title.trim(),
        teaser: teaser.trim() || null,
        field_key: fieldKey,
        chart_type: orderedTypes[0],
        chart_types: orderedTypes,
        audience_channel: audienceChannel.trim() || null,
        audience_filter,
        visibility,
        sort_order: Math.round(Number(sortOrder) || 0),
        active,
        updated_at: new Date().toISOString(),
      };

      if (chart?.id) {
        const { error } = await supabase.from('chart_definitions').update(payload).eq('id', chart.id);
        if (error) throw error;
        toast.success('Chart updated');
      } else {
        const { error } = await supabase.from('chart_definitions').insert(payload);
        if (error) throw error;
        toast.success('Chart created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const filterField = fieldOptions.find((f) => f.key === filterKey);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/40">
      <div className="w-full max-w-4xl max-h-[92vh] flex flex-col rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {chart ? 'Edit chart' : 'New chart'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Content */}
            <section className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Content
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  className={inputClass}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={handleTitleBlur}
                  placeholder="How long have you been fractional?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug</label>
                <input
                  className={`${inputClass} font-mono text-xs`}
                  value={slug}
                  onChange={(e) => setSlug(normalizeChartSlug(e.target.value))}
                  disabled={Boolean(chart)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teaser</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={teaser}
                  onChange={(e) => setTeaser(e.target.value)}
                  placeholder="Short line shown above the chart"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Custom field
                </label>
                <select className={inputClass} value={fieldKey} onChange={(e) => setFieldKey(e.target.value)}>
                  {fieldOptions.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label} ({f.key})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Display formats (1–{MAX_CHART_TYPES})
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Viewers can switch among the formats you enable. Default is used first.
                </p>
                <div className="flex flex-wrap gap-2">
                  {CHART_TYPES.map((t) => {
                    const on = chartTypes.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleChartType(t)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          on
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {chartTypeLabel(t)}
                      </button>
                    );
                  })}
                </div>
                {chartTypes.length > 1 ? (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Default format
                    </label>
                    <select
                      className={inputClass}
                      value={defaultType}
                      onChange={(e) => setDefaultType(e.target.value as ChartType)}
                    >
                      {chartTypes.map((t) => (
                        <option key={t} value={t}>
                          {chartTypeLabel(t)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
            </section>

            {/* Audience & publishing */}
            <section className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Audience & publishing
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Visibility
                </label>
                <select
                  className={inputClass}
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as ChartVisibility)}
                >
                  {CHART_VISIBILITIES.map((v) => (
                    <option key={v} value={v}>
                      {chartVisibilityLabel(v)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Audience channel
                </label>
                <select
                  className={inputClass}
                  value={audienceChannel}
                  onChange={(e) => setAudienceChannel(e.target.value)}
                >
                  <option value="">All subscribers</option>
                  {channelOptions.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.label} ({c.slug})
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Optional filter</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                  Narrow the audience further (e.g. only Submitted).
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Filter field
                  </label>
                  <select className={inputClass} value={filterKey} onChange={(e) => setFilterKey(e.target.value)}>
                    <option value="">None</option>
                    {fieldOptions.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.key}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Filter value
                  </label>
                  {filterField?.options?.length ? (
                    <select
                      className={inputClass}
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                    >
                      <option value="">—</option>
                      {filterField.options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className={inputClass}
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      placeholder="e.g. Submitted"
                      disabled={!filterKey}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort order
                  </label>
                  <input
                    type="number"
                    className={inputClass}
                    value={sortOrder}
                    onChange={(e) => setSortOrder(Number(e.target.value))}
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Active
                  </label>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
